import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixCommentsCategoriesV1CommentsWidget from './comments-categories-v1-comments-widget-widgets.http.js';
// @ts-ignore
import { transformSDKPageURLV2ToRESTPageURLV2 } from '@wix/sdk-runtime/transformations/page-url-v2';
import { transformRESTPageURLV2ToSDKPageURLV2 } from '@wix/sdk-runtime/transformations/page-url-v2';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/** CommentsWidget is the main entity of CommentsWidgetRegistry that can be used for lorem ipsum dolor */
export interface CommentsWidget {
  /**
   * CommentsWidget ID
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Widget category ID
   * @format GUID
   */
  categoryId?: string;
  /**
   * Widget resource ID
   * @minLength 1
   * @maxLength 128
   */
  resourceId?: string;
  /** Path to widget location */
  urlPath?: string;
  /** Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision */
  revision?: string | null;
  /**
   * Represents the time this CommentsResource was created
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Represents the time this CommentsResource was last updated
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Component ID of the widget to which the category is attached to
   * @maxLength 300
   */
  componentId?: string | null;
  /** Count indicating how many published comments exist in category and resource */
  commentCount?: number | null;
}

export interface RegisterCommentsWidgetRequest {
  /**
   * Widget category ID
   * @format GUID
   */
  categoryId: string;
  /**
   * Widget resource ID
   * @minLength 1
   * @maxLength 128
   */
  resourceId: string;
  /**
   * Deprecated field
   * @minLength 1
   * @maxLength 128
   */
  componentId?: string;
  /**
   * Dynamic page tittle, if the widget is inside a dynamic page
   * @maxLength 300
   */
  dynamicPageTitle?: string | null;
  /**
   * PageId of the page, which contains the widget
   * @minLength 1
   * @maxLength 300
   */
  pageId?: string | null;
  /**
   * Page Url, provided by the client
   * @maxLength 500
   */
  urlPath?: string | null;
}

export interface RegisterCommentsWidgetResponse {
  /** The registered CommentsWidget */
  commentsWidget?: CommentsWidget;
}

export interface QueryCommentsWidgetResourcesRequest {
  /** Cursor query */
  query: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryCommentsWidgetResourcesResponse {
  /**
   * List of resources
   * @minLength 1
   * @maxLength 128
   * @maxSize 100
   */
  resourceIds?: string[];
  /** Paging metadata */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface CreateCommentsWidgetRequest {
  /** CommentsWidget to be created */
  commentsWidget: CommentsWidget;
}

export interface CreateCommentsWidgetResponse {
  /** The created CommentsWidget */
  commentsWidget?: CommentsWidget;
}

export interface GetCommentsWidgetRequest {
  /**
   * Id of the CommentsWidget to retrieve
   * @format GUID
   */
  commentsWidgetId: string;
}

export interface GetCommentsWidgetResponse {
  /** The retrieved CommentsWidget */
  commentsWidget?: CommentsWidget;
}

export interface UpdateCommentsWidgetRequest {
  /** CommentsWidget to be updated, may be partial */
  commentsWidget: CommentsWidget;
}

export interface UpdateCommentsWidgetResponse {
  /** The updated CommentsWidget */
  commentsWidget?: CommentsWidget;
}

export interface DeleteCommentsWidgetRequest {
  /**
   * Id of the CommentsWidget to delete
   * @format GUID
   */
  commentsWidgetId: string;
  /** The revision of the CommentsWidget */
  revision?: string;
}

export interface DeleteCommentsWidgetResponse {}

export interface QueryCommentsWidgetRequest {
  /** WQL expression */
  query: QueryV2;
}

export interface QueryV2 extends QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   */
  sort?: Sorting[];
  /** Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned. */
  fields?: string[];
  /** Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned. */
  fieldsets?: string[];
}

/** @oneof */
export interface QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface QueryCommentsWidgetResponse {
  /** The retrieved CommentsWidgets */
  commentsWidgets?: CommentsWidget[];
  /** Paging metadata */
  metadata?: PagingMetadataV2;
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: Cursors;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface CommentsWidgetCreatedEnvelope {
  entity: CommentsWidget;
  metadata: EventMetadata;
}

/** @webhook
 * @eventType wix.comments.categories.v1.comments_widget_created
 * @serviceIdentifier wix.comments.categories.v1.CommentsWidgetRegistry
 * @slug created
 * @documentationMaturity preview
 */
export declare function onCommentsWidgetCreated(
  handler: (event: CommentsWidgetCreatedEnvelope) => void | Promise<void>
): void;

export interface CommentsWidgetDeletedEnvelope {
  metadata: EventMetadata;
}

/** @webhook
 * @eventType wix.comments.categories.v1.comments_widget_deleted
 * @serviceIdentifier wix.comments.categories.v1.CommentsWidgetRegistry
 * @slug deleted
 * @documentationMaturity preview
 */
export declare function onCommentsWidgetDeleted(
  handler: (event: CommentsWidgetDeletedEnvelope) => void | Promise<void>
): void;

export interface CommentsWidgetUpdatedEnvelope {
  entity: CommentsWidget;
  metadata: EventMetadata;
}

/** @webhook
 * @eventType wix.comments.categories.v1.comments_widget_updated
 * @serviceIdentifier wix.comments.categories.v1.CommentsWidgetRegistry
 * @slug updated
 * @documentationMaturity preview
 */
export declare function onCommentsWidgetUpdated(
  handler: (event: CommentsWidgetUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Registers CommentsWidget. Whenever a widget is loaded for the first time or its resourceId changes, it saves the new resource information about the widget
 * @param categoryId - Widget category ID
 * @public
 * @documentationMaturity preview
 * @requiredField categoryId
 * @requiredField options.resourceId
 * @permissionId WIX_COMMENTS.COMMENTS_WIDGET_REGISTER
 * @fqn wix.comments.categories.v1.CommentsWidgetRegistry.RegisterCommentsWidget
 */
export async function registerCommentsWidget(
  categoryId: string,
  options?: NonNullablePaths<RegisterCommentsWidgetOptions, `resourceId`, 2>
): Promise<
  NonNullablePaths<
    RegisterCommentsWidgetResponse,
    `commentsWidget.categoryId` | `commentsWidget.resourceId`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    categoryId: categoryId,
    resourceId: options?.resourceId,
    componentId: options?.componentId,
    dynamicPageTitle: options?.dynamicPageTitle,
    pageId: options?.pageId,
    urlPath: options?.urlPath,
  });

  const reqOpts =
    ambassadorWixCommentsCategoriesV1CommentsWidget.registerCommentsWidget(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'commentsWidget.urlPath' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          categoryId: '$[0]',
          resourceId: '$[1].resourceId',
          componentId: '$[1].componentId',
          dynamicPageTitle: '$[1].dynamicPageTitle',
          pageId: '$[1].pageId',
          urlPath: '$[1].urlPath',
        },
        singleArgumentUnchanged: false,
      },
      ['categoryId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface RegisterCommentsWidgetOptions {
  /**
   * Widget resource ID
   * @minLength 1
   * @maxLength 128
   */
  resourceId: string;
  /**
   * Deprecated field
   * @minLength 1
   * @maxLength 128
   */
  componentId?: string;
  /**
   * Dynamic page tittle, if the widget is inside a dynamic page
   * @maxLength 300
   */
  dynamicPageTitle?: string | null;
  /**
   * PageId of the page, which contains the widget
   * @minLength 1
   * @maxLength 300
   */
  pageId?: string | null;
  /**
   * Page Url, provided by the client
   * @maxLength 500
   */
  urlPath?: string | null;
}

/**
 * Returns all registered widget resource ids contained in metaSite
 * @param query - Cursor query
 * @public
 * @documentationMaturity preview
 * @requiredField query
 * @permissionId WIX_COMMENTS.COMMENTS_WIDGET_RESOURCES_QUERY
 * @fqn wix.comments.categories.v1.CommentsWidgetRegistry.QueryCommentsWidgetResources
 */
export async function queryCommentsWidgetResources(
  query: CursorQuery
): Promise<
  NonNullablePaths<QueryCommentsWidgetResourcesResponse, `resourceIds`, 2>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixCommentsCategoriesV1CommentsWidget.queryCommentsWidgetResources(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates a new CommentsWidget
 * @param commentsWidget - CommentsWidget to be created
 * @public
 * @documentationMaturity preview
 * @requiredField commentsWidget
 * @requiredField commentsWidget.categoryId
 * @requiredField commentsWidget.resourceId
 * @permissionId WIX_COMMENTS.COMMENTS_WIDGET_CREATE
 * @returns The created CommentsWidget
 * @fqn wix.comments.categories.v1.CommentsWidgetRegistry.CreateCommentsWidget
 */
export async function createCommentsWidget(
  commentsWidget: NonNullablePaths<
    CommentsWidget,
    `categoryId` | `resourceId`,
    2
  >
): Promise<NonNullablePaths<CommentsWidget, `categoryId` | `resourceId`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ commentsWidget: commentsWidget }),
    [
      {
        transformFn: transformSDKPageURLV2ToRESTPageURLV2,
        paths: [{ path: 'commentsWidget.urlPath' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixCommentsCategoriesV1CommentsWidget.createCommentsWidget(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'commentsWidget.urlPath' }],
        },
      ])
    )?.commentsWidget!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { commentsWidget: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['commentsWidget']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Get a CommentsWidget by id
 * @param commentsWidgetId - Id of the CommentsWidget to retrieve
 * @public
 * @documentationMaturity preview
 * @requiredField commentsWidgetId
 * @permissionId WIX_COMMENTS.COMMENTS_WIDGET_READ
 * @returns The retrieved CommentsWidget
 * @fqn wix.comments.categories.v1.CommentsWidgetRegistry.GetCommentsWidget
 */
export async function getCommentsWidget(
  commentsWidgetId: string
): Promise<NonNullablePaths<CommentsWidget, `categoryId` | `resourceId`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    commentsWidgetId: commentsWidgetId,
  });

  const reqOpts =
    ambassadorWixCommentsCategoriesV1CommentsWidget.getCommentsWidget(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'commentsWidget.urlPath' }],
        },
      ])
    )?.commentsWidget!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { commentsWidgetId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['commentsWidgetId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Update CommentsWidget, supports partial update
 * Pass the latest `revision` for a successful update
 * @param _id - CommentsWidget ID
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField commentsWidget
 * @requiredField commentsWidget.revision
 * @permissionId WIX_COMMENTS.COMMENTS_WIDGET_EDIT
 * @returns The updated CommentsWidget
 * @fqn wix.comments.categories.v1.CommentsWidgetRegistry.UpdateCommentsWidget
 */
export async function updateCommentsWidget(
  _id: string,
  commentsWidget: NonNullablePaths<UpdateCommentsWidget, `revision`, 2>
): Promise<NonNullablePaths<CommentsWidget, `categoryId` | `resourceId`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      commentsWidget: { ...commentsWidget, id: _id },
    }),
    [
      {
        transformFn: transformSDKPageURLV2ToRESTPageURLV2,
        paths: [{ path: 'commentsWidget.urlPath' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixCommentsCategoriesV1CommentsWidget.updateCommentsWidget(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'commentsWidget.urlPath' }],
        },
      ])
    )?.commentsWidget!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { commentsWidget: '$[1]' },
        explicitPathsToArguments: { 'commentsWidget.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'commentsWidget']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateCommentsWidget {
  /**
   * CommentsWidget ID
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Widget category ID
   * @format GUID
   */
  categoryId?: string;
  /**
   * Widget resource ID
   * @minLength 1
   * @maxLength 128
   */
  resourceId?: string;
  /** Path to widget location */
  urlPath?: string;
  /** Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision */
  revision?: string | null;
  /**
   * Represents the time this CommentsResource was created
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Represents the time this CommentsResource was last updated
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Component ID of the widget to which the category is attached to
   * @maxLength 300
   */
  componentId?: string | null;
  /** Count indicating how many published comments exist in category and resource */
  commentCount?: number | null;
}

/**
 * Delete a CommentsWidget
 * @param commentsWidgetId - Id of the CommentsWidget to delete
 * @public
 * @documentationMaturity preview
 * @requiredField commentsWidgetId
 * @permissionId WIX_COMMENTS.COMMENTS_WIDGET_DELETE
 * @fqn wix.comments.categories.v1.CommentsWidgetRegistry.DeleteCommentsWidget
 */
export async function deleteCommentsWidget(
  commentsWidgetId: string,
  options?: DeleteCommentsWidgetOptions
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    commentsWidgetId: commentsWidgetId,
    revision: options?.revision,
  });

  const reqOpts =
    ambassadorWixCommentsCategoriesV1CommentsWidget.deleteCommentsWidget(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          commentsWidgetId: '$[0]',
          revision: '$[1].revision',
        },
        singleArgumentUnchanged: false,
      },
      ['commentsWidgetId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface DeleteCommentsWidgetOptions {
  /** The revision of the CommentsWidget */
  revision?: string;
}

/**
 * Query CommentsWidgets using [WQL - Wix Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language)
 * @public
 * @documentationMaturity preview
 * @permissionId WIX_COMMENTS.COMMENTS_WIDGET_QUERY
 * @fqn wix.comments.categories.v1.CommentsWidgetRegistry.QueryCommentsWidget
 */
export function queryCommentsWidget(): CommentsWidgetsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    CommentsWidget,
    'CURSOR',
    QueryCommentsWidgetRequest,
    QueryCommentsWidgetResponse
  >({
    func: async (payload: QueryCommentsWidgetRequest) => {
      const reqOpts =
        ambassadorWixCommentsCategoriesV1CommentsWidget.queryCommentsWidget(
          payload
        );

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryCommentsWidgetRequest['query']) => {
      const args = [query, {}] as [QueryCommentsWidgetRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryCommentsWidgetResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [
          {
            transformFn: transformRESTPageURLV2ToSDKPageURLV2,
            paths: [{ path: 'commentsWidgets.urlPath' }],
          },
        ])
      );

      return {
        items: transformedData?.commentsWidgets,
        pagingMetadata: transformedData?.metadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface CommentsWidgetsQueryResult extends QueryCursorResult {
  items: CommentsWidget[];
  query: CommentsWidgetsQueryBuilder;
  next: () => Promise<CommentsWidgetsQueryResult>;
  prev: () => Promise<CommentsWidgetsQueryResult>;
}

export interface CommentsWidgetsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName:
      | '_id'
      | 'categoryId'
      | 'resourceId'
      | 'urlPath'
      | '_createdDate'
      | '_updatedDate'
      | 'componentId'
      | 'commentCount',
    value: any
  ) => CommentsWidgetsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (
    propertyName:
      | '_id'
      | 'categoryId'
      | 'resourceId'
      | 'urlPath'
      | '_createdDate'
      | '_updatedDate'
      | 'componentId'
      | 'commentCount',
    value: any
  ) => CommentsWidgetsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ge: (
    propertyName:
      | '_id'
      | 'categoryId'
      | 'resourceId'
      | '_createdDate'
      | '_updatedDate'
      | 'componentId'
      | 'commentCount',
    value: any
  ) => CommentsWidgetsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  gt: (
    propertyName:
      | '_id'
      | 'categoryId'
      | 'resourceId'
      | '_createdDate'
      | '_updatedDate'
      | 'componentId'
      | 'commentCount',
    value: any
  ) => CommentsWidgetsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  le: (
    propertyName:
      | '_id'
      | 'categoryId'
      | 'resourceId'
      | '_createdDate'
      | '_updatedDate'
      | 'componentId'
      | 'commentCount',
    value: any
  ) => CommentsWidgetsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  lt: (
    propertyName:
      | '_id'
      | 'categoryId'
      | 'resourceId'
      | '_createdDate'
      | '_updatedDate'
      | 'componentId'
      | 'commentCount',
    value: any
  ) => CommentsWidgetsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   * @documentationMaturity preview
   */
  startsWith: (
    propertyName: '_id' | 'categoryId' | 'resourceId' | 'componentId',
    value: string
  ) => CommentsWidgetsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasSome: (
    propertyName:
      | '_id'
      | 'categoryId'
      | 'resourceId'
      | 'urlPath'
      | '_createdDate'
      | '_updatedDate'
      | 'componentId'
      | 'commentCount',
    value: any[]
  ) => CommentsWidgetsQueryBuilder;
  /** @documentationMaturity preview */
  in: (
    propertyName:
      | '_id'
      | 'categoryId'
      | 'resourceId'
      | 'urlPath'
      | '_createdDate'
      | '_updatedDate'
      | 'componentId'
      | 'commentCount',
    value: any
  ) => CommentsWidgetsQueryBuilder;
  /** @documentationMaturity preview */
  exists: (
    propertyName:
      | '_id'
      | 'categoryId'
      | 'resourceId'
      | 'urlPath'
      | '_createdDate'
      | '_updatedDate'
      | 'componentId'
      | 'commentCount',
    value: boolean
  ) => CommentsWidgetsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  ascending: (
    ...propertyNames: Array<
      | '_id'
      | 'categoryId'
      | 'resourceId'
      | 'urlPath'
      | '_createdDate'
      | '_updatedDate'
      | 'componentId'
      | 'commentCount'
    >
  ) => CommentsWidgetsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  descending: (
    ...propertyNames: Array<
      | '_id'
      | 'categoryId'
      | 'resourceId'
      | 'urlPath'
      | '_createdDate'
      | '_updatedDate'
      | 'componentId'
      | 'commentCount'
    >
  ) => CommentsWidgetsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => CommentsWidgetsQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => CommentsWidgetsQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<CommentsWidgetsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.comments.categories.v1.CommentsWidgetRegistry.QueryCommentsWidget
 * @requiredField query
 */
export async function typedQueryCommentsWidget(
  query: CommentsWidgetQuery
): Promise<
  NonNullablePaths<
    QueryCommentsWidgetResponse,
    | `commentsWidgets`
    | `commentsWidgets.${number}.categoryId`
    | `commentsWidgets.${number}.resourceId`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixCommentsCategoriesV1CommentsWidget.queryCommentsWidget(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'commentsWidgets.urlPath' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CommentsWidgetQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: [
        '_createdDate',
        '_id',
        '_updatedDate',
        'categoryId',
        'commentCount',
        'componentId',
        'resourceId',
        'urlPath'
      ];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  CommentsWidget,
  CommentsWidgetQuerySpec
>;
export type CommentsWidgetQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object.

  Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).  
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<
      CommentsWidget,
      CommentsWidgetQuerySpec,
      CommentsWidgetQuery
    >(),
  },
};
